/**
 * NPC memory and relationships — GDD §8, docs/04 §4.
 *
 * This is the system the whole pitch rests on: "the world remembers you."
 * Two design constraints shape the implementation.
 *
 * 1. Memory is BOUNDED (12 slots). Unbounded memory is both a save-size problem
 *    and, more importantly, wrong: people remember a handful of vivid things and
 *    a general impression. Folding old memories into `impression` models that and
 *    caps growth at the same time.
 *
 * 2. Dialogue is ASSEMBLED, not authored per state. Writing lines for every
 *    NPC x trust x resentment x history combination is the cost explosion the
 *    GDD flags in §30. Conditional fragments (see `selectFragments`) turn ~20
 *    authored lines into hundreds of distinct conversations.
 */

import type { EventLog, GameEvent } from './events.js';
import type { FactId, NpcId } from './types.js';

export const MEMORY_SLOTS = 12;

export type RelationshipStage =
  | 'stranger'
  | 'acquaintance'
  | 'companion'
  | 'friend'
  | 'trusted'
  | 'confidant'
  | 'family';

/** Folded summary of memories that no longer fit in the 12 slots. */
export interface Impression {
  /** Net helpfulness the NPC has come to expect from the player. */
  kindness: number;
  /** Net reliability: promises kept vs broken, deals honoured vs not. */
  reliability: number;
  /** Net danger: how violent the player has been within this NPC's view. */
  menace: number;
  /** How many folded memories this impression stands in for. */
  foldedCount: number;
}

export interface MemoryEntry {
  readonly id: string;
  /** Event type that created the memory, so dialogue can query it structurally. */
  readonly eventType: string;
  readonly summary: string;
  readonly tick: number;
  readonly rollRef?: number;
  /**
   * How strongly this memory sticks (0..100). Vivid events resist folding.
   * A murder is never forgotten; a delivered parcel is.
   */
  weight: number;
  /** Sentiment this memory carries, used when folding into an impression. */
  readonly valence: number;
}

export interface NpcPromise {
  readonly id: string;
  readonly description: string;
  /** Game tick the promise comes due. */
  readonly dueTick: number;
  /** Wall-clock ms, for "meet me in 3 days" honouring real time (GDD §8). */
  readonly dueRealMs?: number;
  status: 'open' | 'kept' | 'broken';
}

export interface NpcMemory {
  readonly npcId: NpcId;
  trust: number; // -100..100
  liking: number; // -100..100
  respect: number; // -100..100
  /** Never decays passively. Forgiveness must be an explicit event (GDD §8). */
  resentment: number; // 0..100
  /** What the NPC owes the player. Drives them seeking the player out. */
  debt: number;
  impression: Impression;
  memories: MemoryEntry[];
  promises: NpcPromise[];
  knownFacts: FactId[];
  /** Facts this NPC knows and will lie about if asked (GDD §8: NPCs can lie). */
  concealedFacts: FactId[];
  lastSeenTick: number;
  metCount: number;
  alive: boolean;
  /** Set when the NPC died so descendants can inherit the grudge (GDD §8). */
  diedAtTick?: number;
}

export function createNpcMemory(npcId: NpcId): NpcMemory {
  return {
    npcId,
    trust: 0,
    liking: 0,
    respect: 0,
    resentment: 0,
    debt: 0,
    impression: { kindness: 0, reliability: 0, menace: 0, foldedCount: 0 },
    memories: [],
    promises: [],
    knownFacts: [],
    concealedFacts: [],
    lastSeenTick: -1,
    metCount: 0,
    alive: true,
  };
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export function adjust(
  npc: NpcMemory,
  delta: Partial<Pick<NpcMemory, 'trust' | 'liking' | 'respect' | 'resentment' | 'debt'>>,
): void {
  if (delta.trust !== undefined) npc.trust = clamp(npc.trust + delta.trust, -100, 100);
  if (delta.liking !== undefined) npc.liking = clamp(npc.liking + delta.liking, -100, 100);
  if (delta.respect !== undefined) npc.respect = clamp(npc.respect + delta.respect, -100, 100);
  if (delta.resentment !== undefined) {
    npc.resentment = clamp(npc.resentment + delta.resentment, 0, 100);
  }
  if (delta.debt !== undefined) npc.debt = Math.max(0, npc.debt + delta.debt);
}

/**
 * Add a memory, folding the weakest one into `impression` if we are over budget.
 * Returns the folded memory, if any, so callers can surface "she's starting to
 * forget the details, but not how she felt about it".
 */
export function remember(npc: NpcMemory, entry: MemoryEntry): MemoryEntry | null {
  npc.memories.push(entry);
  if (npc.memories.length <= MEMORY_SLOTS) return null;

  let weakestIdx = 0;
  for (let i = 1; i < npc.memories.length; i++) {
    const a = npc.memories[i]!;
    const b = npc.memories[weakestIdx]!;
    // Lower weight folds first; on a tie the older memory folds.
    if (a.weight < b.weight || (a.weight === b.weight && a.tick < b.tick)) weakestIdx = i;
  }
  const [folded] = npc.memories.splice(weakestIdx, 1) as [MemoryEntry];
  foldIntoImpression(npc.impression, folded);
  return folded;
}

function foldIntoImpression(imp: Impression, m: MemoryEntry): void {
  const scale = m.weight / 100;
  switch (m.eventType) {
    case 'NPC_SAVED':
    case 'NPC_HELPED':
      imp.kindness += 2 * scale;
      break;
    case 'NPC_KILLED':
    case 'MONSTER_KILLED':
      imp.menace += 2 * scale;
      break;
    case 'NPC_BETRAYED':
    case 'PROMISE_BROKEN':
      imp.reliability -= 2 * scale;
      break;
    case 'PROMISE_KEPT':
      imp.reliability += 2 * scale;
      break;
    case 'NPC_ROBBED':
      imp.kindness -= 1.5 * scale;
      imp.reliability -= 1 * scale;
      break;
    case 'NPC_LIED_TO':
      imp.reliability -= 1.5 * scale;
      break;
    default:
      imp.kindness += m.valence * 0.5 * scale;
  }
  imp.foldedCount += 1;
  imp.kindness = clamp(imp.kindness, -100, 100);
  imp.reliability = clamp(imp.reliability, -100, 100);
  imp.menace = clamp(imp.menace, 0, 100);
}

/** GDD §8 progression. Resentment vetoes closeness regardless of liking. */
export function relationshipStage(npc: NpcMemory): RelationshipStage {
  if (npc.resentment >= 60) return 'stranger';
  const bond = (npc.trust + npc.liking) / 2;
  if (npc.metCount === 0) return 'stranger';
  if (bond >= 85 && npc.trust >= 80) return 'family';
  if (bond >= 70) return 'confidant';
  if (bond >= 55) return 'trusted';
  if (bond >= 35) return 'friend';
  if (bond >= 15) return 'companion';
  if (npc.metCount >= 2) return 'acquaintance';
  return 'stranger';
}

/** Will this NPC share a secret right now? */
export function willShareSecret(npc: NpcMemory): boolean {
  const stage = relationshipStage(npc);
  return (stage === 'confidant' || stage === 'family' || stage === 'trusted') && npc.trust >= 55;
}

/** GDD §8: NPCs can lie. Low trust plus high resentment makes deception likely. */
export function willLie(npc: NpcMemory, factId: FactId): boolean {
  if (!npc.concealedFacts.includes(factId)) return false;
  return npc.trust < 40 || npc.resentment >= 30;
}

export function makePromise(npc: NpcMemory, promise: NpcPromise): void {
  npc.promises.push(promise);
}

/**
 * Resolve promises that have come due. A promise the player did not honour
 * becomes resentment — this is the machinery behind "you said three days".
 */
/**
 * 기한이 지난 채 아직 열려 있는 약속을 **찾기만** 한다.
 *
 * 전에는 여기서 곧바로 `p.status = 'broken'` 과 신뢰도 -25 까지 적용했다.
 * 그런데 `PROMISE_BROKEN` 리듀서가 똑같은 일을 또 한다 — 살아 있는 세션에서는
 * 벌점이 **두 번** 붙고, 로그를 리플레이하면 한 번만 붙었다. 세이브를 불러오면
 * 미라의 신뢰도가 -50 에서 -25 로 조용히 바뀌어 있었다는 뜻이다.
 *
 * 절대 규칙 3: 상태는 `append(event) → reduce` 로만 바뀐다. 탐지와 적용을
 * 한 함수가 겸하면 그 규칙을 어기지 않고는 쓸 수가 없다. 그래서 이 함수는
 * 이제 아무것도 바꾸지 않는다 — 적용은 리듀서 하나가 책임진다.
 */
export function duePromises(
  npc: NpcMemory,
  nowTick: number,
  nowRealMs?: number,
): NpcPromise[] {
  return npc.promises.filter((p) => {
    if (p.status !== 'open') return false;
    const tickDue = nowTick >= p.dueTick;
    const realDue = p.dueRealMs !== undefined && nowRealMs !== undefined && nowRealMs >= p.dueRealMs;
    return tickDue || realDue;
  });
}

export function keepPromise(npc: NpcMemory, promiseId: string): boolean {
  const p = npc.promises.find((x) => x.id === promiseId && x.status === 'open');
  if (!p) return false;
  p.status = 'kept';
  adjust(npc, { trust: +20, liking: +15, respect: +10 });
  return true;
}

export function hasBrokenPromise(npc: NpcMemory): boolean {
  return npc.promises.some((p) => p.status === 'broken');
}

// ---------------------------------------------------------------------------
// Conditional dialogue assembly
// ---------------------------------------------------------------------------

export interface DialogueFragment {
  /** Condition expression. See `evaluateCondition` for the supported grammar. */
  readonly when?: string;
  readonly text: string;
  /** Higher priority wins when several fragments match the same slot. */
  readonly priority?: number;
  /** Fragments in the same slot are mutually exclusive. Default slot: 'body'. */
  readonly slot?: string;
  /** Only offer this fragment once per run. */
  readonly once?: boolean;
}

export interface DialogueContext {
  readonly npc: NpcMemory;
  readonly log: EventLog;
  readonly tick: number;
  readonly flags: ReadonlySet<string>;
  readonly usedFragments: ReadonlySet<string>;
  /**
   * Facts the PLAYER has learned, kept separate from `npc.knownFacts` so that
   * `knows:` (what this character knows) and `playerKnows:` (what you know) stay
   * distinguishable — the two drive very different lines.
   */
  readonly playerFacts?: ReadonlySet<FactId>;
}

/**
 * Tiny, deliberately non-Turing-complete condition language.
 *
 * Supported:
 *   trust >= 40            numeric comparison on npc fields
 *   metCount == 1
 *   stage == friend        relationship stage equality
 *   event:NPC_SAVED        did this event ever happen to this NPC
 *   event:BOSS_SPARED(*)   did it happen to anyone
 *   promiseBroken          this NPC has a broken promise
 *   flag:sawTheMirror      world flag set
 *   knows:fact.theShattering     this NPC knows the fact
 *   playerKnows:fact.theShattering  the PLAYER has learned the fact
 *   !<anything>            negation
 *   a && b                 conjunction (no precedence beyond left-to-right)
 *   a || b                 disjunction
 *
 * A data-driven condition language rather than JS callbacks is what keeps GOD-4's
 * gate honest: new content must be addable as JSON with zero TypeScript changes,
 * and UGC (GOD-8) must not be able to execute arbitrary code.
 */
export function evaluateCondition(expr: string | undefined, ctx: DialogueContext): boolean {
  if (!expr || expr.trim() === '') return true;
  return evalOr(expr, ctx);
}

function evalOr(expr: string, ctx: DialogueContext): boolean {
  const parts = splitTop(expr, '||');
  if (parts.length > 1) return parts.some((p) => evalAnd(p, ctx));
  return evalAnd(expr, ctx);
}

function evalAnd(expr: string, ctx: DialogueContext): boolean {
  const parts = splitTop(expr, '&&');
  if (parts.length > 1) return parts.every((p) => evalAtom(p, ctx));
  return evalAtom(expr, ctx);
}

/** Split on a delimiter, ignoring anything inside parentheses. */
function splitTop(expr: string, delim: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i]!;
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (depth === 0 && expr.startsWith(delim, i)) {
      out.push(expr.slice(start, i));
      i += delim.length - 1;
      start = i + 1;
    }
  }
  out.push(expr.slice(start));
  return out.length > 1 ? out.map((s) => s.trim()).filter((s) => s.length > 0) : [expr];
}

const NUMERIC_FIELDS = ['trust', 'liking', 'respect', 'resentment', 'debt', 'metCount'] as const;
type NumericField = (typeof NUMERIC_FIELDS)[number];

function evalAtom(raw: string, ctx: DialogueContext): boolean {
  let expr = raw.trim();

  /*
   * 부정을 **먼저** 벗긴다.
   *
   * 전에는 괄호 검사가 먼저였다. 그래서 `!(a & b)` 는 `(` 로 시작하지 않으니
   * 괄호 분기를 타지 못하고, `!` 를 벗긴 `(a & b)` 가 그대로 `evalPositive` 로
   * 넘어갔다. 거기엔 괄호를 읽는 규칙이 없으니 언제나 false 가 나오고,
   * 부정이 붙어 **`!(...)` 조건은 무조건 참**이 됐다.
   */
  let negate = false;
  while (expr.startsWith('!')) {
    negate = !negate;
    expr = expr.slice(1).trim();
  }

  const result =
    expr.startsWith('(') && expr.endsWith(')')
      ? evalOr(expr.slice(1, -1), ctx)
      : evalPositive(expr, ctx);
  return negate ? !result : result;
}

function evalPositive(expr: string, ctx: DialogueContext): boolean {
  const { npc, log, flags } = ctx;

  if (expr === 'promiseBroken') return hasBrokenPromise(npc);
  if (expr === 'promiseOpen') return npc.promises.some((p) => p.status === 'open');
  if (expr === 'alive') return npc.alive;
  if (expr === 'willShareSecret') return willShareSecret(npc);

  if (expr.startsWith('flag:')) return flags.has(expr.slice(5).trim());
  if (expr.startsWith('knows:')) return npc.knownFacts.includes(expr.slice(6).trim());
  if (expr.startsWith('playerKnows:')) {
    return ctx.playerFacts?.has(expr.slice(12).trim()) ?? false;
  }
  if (expr.startsWith('conceals:')) return npc.concealedFacts.includes(expr.slice(9).trim());

  if (expr.startsWith('event:')) {
    const body = expr.slice(6).trim();
    const m = /^([A-Z_]+)(?:\(([^)]*)\))?$/.exec(body);
    if (!m) return false;
    const type = m[1]!;
    const arg = (m[2] ?? 'self').trim();
    const target = arg === '' || arg === 'self' ? npc.npcId : arg === '*' ? undefined : arg;
    return log.happened(type as GameEvent['type'], target);
  }

  if (expr.startsWith('memory:')) {
    const type = expr.slice(7).trim();
    return npc.memories.some((m) => m.eventType === type);
  }

  const stageMatch = /^stage\s*(==|!=)\s*([a-z]+)$/.exec(expr);
  if (stageMatch) {
    const eq = relationshipStage(npc) === stageMatch[2];
    return stageMatch[1] === '==' ? eq : !eq;
  }

  const cmp = /^([a-zA-Z]+)\s*(>=|<=|==|!=|>|<)\s*(-?\d+)$/.exec(expr);
  if (cmp) {
    const field = cmp[1] as NumericField;
    if (!NUMERIC_FIELDS.includes(field)) return false;
    const left = npc[field];
    const right = Number(cmp[3]);
    switch (cmp[2]) {
      case '>=':
        return left >= right;
      case '<=':
        return left <= right;
      case '>':
        return left > right;
      case '<':
        return left < right;
      case '==':
        return left === right;
      case '!=':
        return left !== right;
    }
  }

  return false;
}

export interface SelectedFragment {
  readonly slot: string;
  readonly text: string;
  readonly index: number;
}

/**
 * Pick at most one fragment per slot: the highest-priority match, ties broken by
 * declaration order so authors can rely on "put the specific line first".
 */
export function selectFragments(
  fragments: readonly DialogueFragment[],
  ctx: DialogueContext,
): SelectedFragment[] {
  const bySlot = new Map<string, SelectedFragment & { priority: number }>();

  fragments.forEach((frag, index) => {
    const slot = frag.slot ?? 'body';
    const key = `${slot}#${index}`;
    if (frag.once && ctx.usedFragments.has(key)) return;
    if (!evaluateCondition(frag.when, ctx)) return;

    // Unconditional fragments act as fallbacks, so they lose to any real match.
    const priority = frag.priority ?? (frag.when ? 10 : 0);
    const existing = bySlot.get(slot);
    if (!existing || priority > existing.priority) {
      bySlot.set(slot, { slot, text: frag.text, index, priority });
    }
  });

  return [...bySlot.values()]
    .sort((a, b) => a.index - b.index)
    .map(({ slot, text, index }) => ({ slot, text, index }));
}

export function fragmentKey(frag: DialogueFragment, index: number): string {
  return `${frag.slot ?? 'body'}#${index}`;
}
